'use client';

import {
	BigNum,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { useEffect } from 'react';
import useGetOraclePriceForMarket from './useGetOraclePriceForMarket';
import useSPLTokenBalance from './useSPLTokenBalance';
import { dlog } from '../dev';
import useVelocityStore from '../stores/VelocityStore/useVelocityStore';
import { useUniqueSpotMarkets } from './spotMarkets/useUniqueSpotMarkets';
import { useSetWalletBalance } from './query/walletBalances/useWalletBalance';

export const useSyncSpotMarketBalance = (
	spotMarketConfig: SpotMarketConfig
) => {
	const setWalletBalance = useSetWalletBalance();
	// Pass decimals to enable direct WebSocket accountData parsing (avoids RPC call on updates)
	const decimals = spotMarketConfig.precisionExp.toNumber();
	const [splTokenBalance] = useSPLTokenBalance(spotMarketConfig.mint, decimals);
	const walletSolBalance = useVelocityStore((s) => s.wallet.currentSolBalance);
	const marketId = MarketId.createSpotMarket(spotMarketConfig.marketIndex);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	const updateBalances = (baseBalance: number) => {
		if (baseBalance === undefined) return;

		const priceToUse = getOraclePriceForMarket(marketId);

		const quoteValue = baseBalance * (priceToUse?.toNum() ?? 0);

		setWalletBalance(spotMarketConfig.symbol, {
			base: BigNum.fromPrint(
				baseBalance.toString(),
				spotMarketConfig.precisionExp
			),
			quote: BigNum.fromPrint(quoteValue.toString(), QUOTE_PRECISION_EXP),
		});
	};

	// only need an update when the wallet balance changes, or on a large time interval, rather than on every price movement
	// large time interval should account for major price movements of wallet collateral
	useEffect(() => {
		const newBalance =
			spotMarketConfig.symbol === 'SOL'
				? (walletSolBalance?.toNum() ?? 0)
				: splTokenBalance;

		dlog(
			`spl_subscription`,
			`useSyncSpotMarketBalance :: ${spotMarketConfig.symbol} :: ${newBalance}`
		);

		updateBalances(newBalance);
	}, [splTokenBalance, spotMarketConfig.marketIndex, walletSolBalance]);
};

// TODO: Refactor to single hook with useEffect that directly subscribes to Observables
// instead of using component wrappers.

// Component wrapper to properly call hook per spot market (follows Rules of Hooks)
const SyncSpotMarketBalance = ({
	spotMarketConfig,
}: {
	spotMarketConfig: SpotMarketConfig;
}) => {
	useSyncSpotMarketBalance(spotMarketConfig);
	return null;
};

// Keeps all the balances for all velocity spot markets in sync via React Query cache.
export const SyncWalletBalances = () => {
	const uniqueSpotMarkets = useUniqueSpotMarkets();
	return (
		<>
			{uniqueSpotMarkets.map((spotMarket) => (
				<SyncSpotMarketBalance
					key={spotMarket.marketIndex}
					spotMarketConfig={spotMarket}
				/>
			))}
		</>
	);
};
